import { FallbackSuspense } from '@newfront-insurance/admin-ui/layout';
import { GeneralAppProviders } from '@newfront-insurance/app-providers';
import { isNotUndefined } from '@newfront-insurance/core';
import { initializeDatadogLogsAndRUM } from '@newfront-insurance/dd-rum';
import { registerLocale } from 'i18n-iso-countries';
import enLang from 'i18n-iso-countries/langs/en.json';
import type { AppProps } from 'next/app';
import { Suspense, lazy, useEffect, useState, type JSX } from 'react';

import { HeadTitle } from '@/client/components/head-title';
import { Providers } from '@/client/providers';
import { AuthProvider } from '@/client/providers/auth';
import { TRPCProvider } from '@/client/providers/trpc';
import { getConfig } from '@/config';
import '@newfront-insurance/tailwind-config/globals.css';
import 'katex/dist/katex.min.css';
import 'streamdown/styles.css';
import '../client/global.css';

registerLocale(enLang);

initializeDatadogLogsAndRUM('ACCOUNTS');

const config = getConfig();

const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/production').then((d) => ({
    default: d.ReactQueryDevtools,
  })),
);

// Extend the Window interface to include toggleDevtools
declare global {
  interface Window {
    toggleDevtools: () => void;
  }
}

/**
 * The component will wrap every page that is rendered. This component stays alive for the entire session. This should
 * be used to initialize providers that are used by the entire application..
 */
export default function App(props: AppProps): JSX.Element | null {
  const { Component } = props;
  const [showDevtools, setShowDevtools] = useState(false);
  useEffect(() => {
    if (isNotUndefined(window)) {
      window.toggleDevtools = () => setShowDevtools((old) => !old);
    }
  }, []);

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const requiresAuth = (Component as any).requiresAuth !== false;

  const innerContent = (
    <>
      <HeadTitle title="Loading..." />
      <FallbackSuspense>
        <Component />
        {config.REACT_QUERY.ENABLE_DEVTOOLS && showDevtools && (
          <Suspense fallback={null}>
            <ReactQueryDevtoolsProduction styleNonce="react-query-devtools-production" />
          </Suspense>
        )}
      </FallbackSuspense>
    </>
  );

  return (
    <GeneralAppProviders
      authProvider={AuthProvider}
      config={config}
      trpcProvider={TRPCProvider}
      useConditionalAuthProvider={false}
      analyticsAppName="accounts"
      withLoginBoundary={requiresAuth}
    >
      {requiresAuth ? <Providers>{innerContent}</Providers> : <>{innerContent}</>}
    </GeneralAppProviders>
  );
}
